import * as React from "react";
import type DeviceTemplate from "../models/DeviceTemplate";
import type TemplateGroup from "../models/TemplateGroup";
import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";
import BaseModel from "../models/BaseModel";
import Logger from "../Logger";
import { ConvertVersion } from "../KnxProductdata";
import ActorDialog from "../modelDialogs/ActorDialog";
import { isUiModeAdmin, isUiModeProductAdmin } from "../uiMode";
import Device from "../models/Device";
import TreeMenuIcon from "../components/NavigationTree2/TreeMenuIcon";
import TemplatePropertyView from "../modelPropertyViews/TemplatePropertyView";
import { itx } from "../translationItx/itx";
import { useEffect } from "react";
import { useRedraw } from "../models/useRerender";
import { EtsImportProductIdPrefix } from "../models/Constants";
import { ApplicationMode } from "../models/SettingsEnums";

const logger = new Logger();

export default class TemplateTreeViewModel extends TreeViewModel {
  get typedObj(): DeviceTemplate {
    return this.obj as DeviceTemplate;
  }

  get isFolder(): boolean {
    return true;
  }

  getTagNeeded(): boolean {
    return false;
  }

  getDisableEdit(): boolean {
    return true;
  }

  allowDrag(): boolean {
    return true;
  }

  private acceptsDevice(obj: BaseModel | undefined): obj is Device {
    if (!(obj instanceof Device) || obj.isDeleted || this.typedObj.isDeleted) return false;
    const template = this.typedObj;
    return (
      (obj.isActor && template.isActor) ||
      (obj.isButton &&
        template.isButton &&
        obj.isSensor === template.isSensor &&
        obj.isDecentralActor === template.isDecentralActor)
    );
  }

  allowDrop(event: any): boolean {
    try {
      // During dragover browsers protect the payload; use the navigator's drag data then.
      const data = JSON.parse(event.dataTransfer?.getData("text/json") || state.dragData || "{}");
      return this.acceptsDevice(state.objectRegistry.getObject(data.id));
    } catch {
      return false;
    }
  }

  async handleDrop2(_mode: DragMode, obj: BaseModel): Promise<DropResult> {
    if (!this.acceptsDevice(obj)) return new DropResult(false);
    await state.undoManager.runAsSingleStep(
      itx("templateTreeViewModel.assignProduct", "Produkt zuweisen"),
      async () => {
        // The property setter delegates to this async method; await it to finish channel updates.
        const lastTemplate = (obj as Device).template;
        await obj.setTemplate(this.typedObj);
        this.typedObj.raiseOnChangeUI("childCollection");
        lastTemplate?.raiseOnChange("childCollection");
      },
    );
    return new DropResult(true);
  }

  dragStart(
    e: any,
    effectAllowed: string = "copyMove", // no itx
  ) {
    super.dragStart(e, "copy"); // no itx
  }

  get childCollection(): Array<BaseModel> | undefined {
    const res = this.typedObj.getUsages();

    res.forEach((ref) => {
      const device = ref.referencedObject as Device;

      ref.additionalInlineButtons = [
        <TreeMenuIcon
          onClick={() => {
            if (device) {
              device.configTemplate = true;
            }
            device.template = undefined;
            device.raiseOnChange("template");
            this.typedObj.raiseOnChange("childCollection");
          }}
          iconName="unlink" // no itx
          tooltipText={"T1 von diesem Produkt lösen".replaceAll("T1", `${device?.fullTag} ${device?.name}`)} // no itx
          objName=""
          key="unlink"
        />,
      ];
      if (isUiModeProductAdmin()) {
        ref.additionalInlineButtons?.push(
          <TreeMenuIcon
            onClick={() => {
              const device = ref.referencedObject as Device;
              if (device) {
                device.configTemplate = true;
              }
              const id = device.id;
              if (state.activePanes.isOpen(id)) {
                state.activePanes.activatePane(id);
              } else {
                state.activePanes.openPane(device);
              }
            }}
            iconName="laptop-code" // no itx
            // tooltipKey={tooltipKey}
            tooltipText={itx("templateTreeViewModel.editProductConfig", "Edit Product Config")}
            objName="ProductConfig" // no itx
            key="ProductConfig"
          />,
        );
      }
    });

    return res;
  }

  get iconUrl(): string | undefined {
    if (this.typedObj.isSensor) {
      return state.hostBaseUrl + "images/consumer/Praesenzmelder.png";
    } else if (this.typedObj.isDecentralActor) {
      return state.hostBaseUrl + "images/buttons/touch-actor.png"; // no itx
    } else if (this.typedObj.isButton) {
      return state.hostBaseUrl + "images/buttons/touch-6.png";
      //      return this.typedObj.image;
    } else {
      return undefined;
    }
  }

  // getPropertyControl(setPinned: (pinned: boolean) => void): any {
  //   return <TemplatePropertyView obj={this.obj} setPinned={setPinned} />;
  // }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // inline menu buttons
  //
  ////////////////////////////////////////////////////////////////////////////////////////

  getButtons() {
    const buttons: JSX.Element[] = [];
    if (this.typedObj.parent?.className === "TemplateGroup") {
      // Filled star for the current favorite, outline star otherwise; clicking toggles it.
      const isFavorite = String(this.typedObj.tag).trim() === "0";
      buttons.push(
        <TreeMenuIcon
          key="favorite"
          icon={<i className={`${isFavorite ? "las" : "lar"} la-star`}></i>}
          tooltipText={isFavorite
            ? itx("templateGroup.unfavorite", "Favorit entfernen")
            : itx("templateGroup.favorite", "Als Favorit festlegen")}
          objName={this.typedObj.name}
          onClick={() => {
            const group = this.typedObj.parent as TemplateGroup;
            group.favoriteTemplate = isFavorite ? undefined : this.typedObj;
          }}
        />,
      );
    }
    if (this.typedObj.genericParams) {
      buttons.push(super.inlineButton_Properties());
    } else if (isUiModeProductAdmin()) {
      buttons.push(super.inlineButton_Open("inlineButton.openProductSettings")); // no itx
    }
    return buttons;
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  async handleProperties() {
    const template = this.typedObj;
    if (!template.genericParams) return;
    const { default: GenericDeviceDialog } = await import("../modelDialogs/GenericDeviceDialog");
    await state.dialogManager.openDialog(
      <GenericDeviceDialog
        initialParams={template.genericParams}
        initialName={template.name}
        productType={template.genericProductType}
        onSelect={(product) => {
          if (!product.genericParams) return;
          state.undoManager.runAsSingleStep(itx("genericDevice.title", "Generisches Produkt"), () => {
            template.name = product.orderNumber;
            template.genericProductType = product.genericProductType ?? "Button";
            template.genericParams = product.genericParams;
          });
        }}
      />,
      true,
    );
  }

  canBePastedInteractive(copiedObjClassName: string): boolean {
    if (copiedObjClassName == "DeviceTemplate") {
      return true;
    }

    return false;
  }

  handleOpen(): boolean {
    if (isUiModeProductAdmin()) {
      this.typedObj.createDummyDevice();

      const id = this.typedObj.id;

      if (state.activePanes.isOpen(id)) {
        state.activePanes.activatePane(id);
      } else {
        state.activePanes.openPane(this.typedObj);
      }
      return true;
    } else {
      return false;
    }
  }

  // handleOpen(): boolean {
  //   if (isUiModeProductAdmin()) {

  //     return super.handleOpen();
  //   } else {
  //     return false;
  //   }
  // }

  async getDisabledContextMenuItems(): Promise<Array<string>> {
    const copiedObjClassName = state.clipboard.getClassName();
    const disabledContextMenuItems = new Array<string>();
    if (copiedObjClassName != "DeviceTemplate") {
      disabledContextMenuItems.push("paste"); // no itx
    }
    if (!(this.typedObj as DeviceTemplate).canBeDeletedInteractive()) {
      disabledContextMenuItems.push("delete"); // no itx
    }

    return disabledContextMenuItems;
  }

  get contextMenuItems(): any {
    let subLabelPaste = undefined;
    try {
      subLabelPaste = state.contextMenuManager.clipboardData.items[0].name;
    } catch (ex) {
      logger.error(ex);
    }

    const items = [
      this.baseContextMenuItems_Copy(),
      this.baseContextMenuItems_Paste(subLabelPaste),
      this.baseContextMenuItems_Delete(),
    ];

    if (isUiModeAdmin()) {
      items.unshift(this.baseContextMenuItems_Open());
    }

    return {
      items: items,
    };
  }

  getTitlePrefix(): string | JSX.Element {
    return (
      <>
        {String(this.typedObj.tag).trim() === "0" && <span aria-label="Favorit">★ </span>}
        <PostFixElement template={this.typedObj} />
      </>
    );
  }
}

function PostFixElement(props: { template: DeviceTemplate }): JSX.Element | null {
  const redraw = useRedraw();

  useEffect(() => {
    // no side effects
    if (!template.knxProductData.nonPersistentXmlData) {
      template.createDummyDevice();
      template.knxProductData.loadXmlData(template.dummyDevice!).then(() => {
        redraw();
      });
    }
  }, []);

  const { template } = props;
  if (state.settings.applicationMode === ApplicationMode.ConceptOne) return null;

  let imgSrcWarning = "/images/warning.png";
  const imageAlert = "/images/alert.png"; // no itx

  if (template.knxProductData.failedLoadingProductData) {
    return (
      <div style={{ display: "flex", flexDirection: "row" }}>
        <img
          src={imgSrcWarning}
          data-tooltip-id="tv-tooltip"
          data-tooltip-html={itx("ServerTemplateList.unknownApplication", "No Application availiable")}
          data-tooltip-place="bottom"
          style={{
            width: "25px",
            height: "25px",
            marginLeft: "10px",
            position: "relative",
          }}
        />
      </div>
    );
  }

  const isEtsImported = template.knxProductData.modelDataId?.startsWith(EtsImportProductIdPrefix);

  if (!isEtsImported) {
    if (template.knxProductData.hasTranslationEN) {
      return null;
    }

    if (!template.knxProductData.nonPersistentXmlData) {
      return null;
    }
  }

  let s = "";

  if (isEtsImported) {
    s = "Dieses Produkt wurde aus ETS importiert und ist nicht von Estada freigegeben.";
  }
  if (template.knxProductData.getApplicationProgramIntern("") == undefined) {
    if (s) {
      s += "\n";
    }
    s += itx("TemplateTreeviewModel.thisProductNoApplication", "Dieses Produkt hat keine Anwendung.");

    imgSrcWarning = "/images/warning.png"; // no itx
  } else if (!template.knxProductData.hasTranslationEN) {
    if (s) {
      s += "\n";
    }
    s += itx(
      "TemplateTreeviewModel.thisProductNoTranslation",
      "Dieses Produkt hat keine englische Übersetzung in den KNX Produktdaten. <br/> Es wird nur sehr eingeschränkt unterstützt.",
    );

    imgSrcWarning = "/images/warning.png"; // no itx
  }

  s = s.replaceAll("\n", "<br/> "); // Tooltip kann keine Zeilenumbrüche

  return (
    <div style={{ display: "flex", flexDirection: "row" }}>
      <img
        src={imgSrcWarning}
        data-tooltip-id="tv-tooltip"
        data-tooltip-html={s}
        data-tooltip-place="bottom"
        data-tooltip-variant="warning" // no itx
        style={{
          width: "25px",
          height: "25px",
          marginLeft: "10px",
          position: "relative",
        }}
      />
    </div>
  );
}
